import React, { useEffect, useState } from 'react';
import { fetchClientesRetorno, ClienteRetorno } from '../../../services/citasService';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { User, Calendar, Clock, BarChart2, ArrowRight, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';

const formatoFechaEspanol = (fechaStr: string | null | undefined): string => {
    if (!fechaStr) return '-';
    const fechaLocalStr = fechaStr.includes('T') ? fechaStr : `${fechaStr}T00:00:00`;
    const fecha = new Date(fechaLocalStr);
    if (isNaN(fecha.getTime())) return fechaStr;
    return format(fecha, "d 'de' MMMM, yyyy", { locale: es });
};

const RetornoClientesPage = () => {
    const [clientesRetorno, setClientesRetorno] = useState<ClienteRetorno[]>([]);
    const [loadingClientes, setLoadingClientes] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const loadClientesRetorno = async () => {
            setLoadingClientes(true);
            try {
                const clientes = await fetchClientesRetorno();
                const clientesOrdenados = clientes.sort((a, b) => b.cliente_id - a.cliente_id);
                setClientesRetorno(clientesOrdenados);
            } catch (error) {
                /* Browser diagnostics omitted. */
            } finally {
                setLoadingClientes(false);
            }
        };
        loadClientesRetorno();
    }, []);

    // Filtrar clientes por nombre
    const filteredClientes = clientesRetorno.filter(cliente =>
        cliente.cliente_nombre.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-xl font-bold text-gray-800">Clientes con Retorno Probable</h2>
                    <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                        <a href="/dashboard" className="hover:text-[#c1348b]">Dashboard</a>
                        <span>/</span>
                        <a href="/kardex-citas" className="hover:text-blue-600">Kardex Citas</a>
                        <span>/</span>
                        <span className="text-blue-600">Retorno Clientes</span>
                    </div>
                </div>

                <div className="bg-indigo-50 px-4 py-2 rounded-full text-indigo-700 font-medium flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full bg-indigo-600 animate-pulse"></span>
                    {clientesRetorno.length} clientes con retorno probable
                </div>
            </div>

            {/* Barra de búsqueda */}
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
                <div className="relative">
                    <input
                        type="text"
                        placeholder="Buscar clientes por nombre..."
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <div className="absolute right-3 top-2.5 text-gray-400">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                </div>
            </div>

            {loadingClientes ? (
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
                </div>
            ) : filteredClientes.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center border border-gray-100">
                    <User className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                        {searchTerm ? "No se encontraron clientes" : "No hay clientes con retorno probable"}
                    </h3>
                    <p className="text-gray-600 max-w-md mx-auto">
                        {searchTerm
                            ? "Intenta con otro término de búsqueda"
                            : "No se encontraron clientes con alta probabilidad de retorno."}
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredClientes.map((cliente) => (
                        <div
                            key={cliente.cliente_id}
                            className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md"
                        >
                            {/* Header tarjeta */}
                            <div className="px-5 py-3 flex justify-between items-center"
                                style={{ background: 'linear-gradient(135deg, #8f1f6415, #61257b15)' }}>
                                <div className="flex items-center gap-2">
                                    <div className="p-2 rounded-full" style={{ background: '#8f1f6420' }}>
                                        <User className="h-5 w-5" style={{ color: '#8f1f64' }} />
                                    </div>
                                    <div>
                                        <span className="font-bold text-gray-900 text-sm">{cliente.cliente_nombre}</span>
                                        {cliente.tipo_nivel_usuario && (
                                            <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold"
                                                style={{ background: '#61257b20', color: '#61257b' }}>
                                                {cliente.tipo_nivel_usuario}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <span className="text-gray-400 text-xs">ID: {cliente.cliente_id}</span>
                            </div>

                            {/* Badge Declaración Jurada */}
                            <div className="px-5 pt-3">
                                {cliente.declaracion_jurada_firmada ? (
                                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg"
                                        style={{ background: '#dcfce7', border: '1px solid #bbf7d0' }}>
                                        <span className="text-sm">✅</span>
                                        <div>
                                            <p className="text-xs font-bold" style={{ color: '#15803d' }}>Dec. Jurada al día</p>
                                            <p className="text-[10px]" style={{ color: '#166534' }}>
                                                {cliente.fecha_declaracion_jurada
                                                    ? `Firmada el ${new Date(cliente.fecha_declaracion_jurada).toLocaleDateString('es-ES')}`
                                                    : 'No requiere nueva firma al regresar'}
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg"
                                        style={{ background: '#fef9c3', border: '1px solid #fde047' }}>
                                        <span className="text-sm">📋</span>
                                        <div>
                                            <p className="text-xs font-bold" style={{ color: '#713f12' }}>Firma pendiente</p>
                                            <p className="text-[10px]" style={{ color: '#92400e' }}>Solicitar firma al retornar</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="p-5">
                                <div className="space-y-3 mt-1">
                                    <div className="flex items-start">
                                        <div className="bg-gray-100 p-2 rounded-lg mr-3">
                                            <RefreshCw className="h-5 w-5 text-gray-600" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">Frecuencia promedio</p>
                                            <p className="font-medium text-gray-900">
                                                {cliente.frecuencia_promedio_dias.toFixed(1)} días
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-start">
                                        <div className="bg-gray-100 p-2 rounded-lg mr-3">
                                            <BarChart2 className="h-5 w-5 text-gray-600" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">Citas totales</p>
                                            <p className="font-medium text-gray-900">
                                                {cliente.cantidad_citas}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-start">
                                        <div className="bg-gray-100 p-2 rounded-lg mr-3">
                                            <Calendar className="h-5 w-5 text-gray-600" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">Última visita</p>
                                            <p className="font-medium text-gray-900">
                                                {formatoFechaEspanol(cliente.ultima_cita_inicio)} -{' '}
                                                {formatoFechaEspanol(cliente.ultima_cita_fin)}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-start">
                                        <div className="bg-gray-100 p-2 rounded-lg mr-3">
                                            <Clock className="h-5 w-5 text-gray-600" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">Próxima visita estimada</p>
                                            <p className="font-medium text-green-700">
                                                {cliente.prox_cita_min && cliente.prox_cita_max
                                                    ? `${formatoFechaEspanol(cliente.prox_cita_min)} - ${formatoFechaEspanol(cliente.prox_cita_max)}`
                                                    : formatoFechaEspanol(cliente.prox_cita_estimada)}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-5 pt-4 border-t border-gray-100 flex justify-between items-center">
                                    <div className="flex items-center">
                                        <span className="text-xs text-gray-500 mr-2">Categoría:</span>
                                        <span
                                            className={`text-sm font-medium ${cliente.categoria_frecuencia_predicha === 'Alto'
                                                    ? 'text-green-600'
                                                    : cliente.categoria_frecuencia_predicha === 'Medio'
                                                        ? 'text-yellow-600'
                                                        : 'text-red-600'
                                                }`}
                                        >
                                            {cliente.categoria_frecuencia_predicha || 'Desconocido'}
                                        </span>
                                    </div>

                                    <Link
                                        to={`/kardex-citas/detalle/${cliente.cliente_id}`}
                                        className="font-bold text-sm flex items-center gap-1 px-3 py-1.5 rounded-lg transition-all"
                                        style={{ color: '#8f1f64', background: '#8f1f6410' }}
                                    >
                                        Ver Kardex <ArrowRight className="ml-1 h-4 w-4" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default RetornoClientesPage;